import { useCallback, useRef, useState } from "react";

/**
 * usePTT — Push-to-Talk audio capture + WebSocket broadcast
 *
 * @param {WebSocket|null} ws  - active WebSocket connection
 * @param {function} onEvent  - called with JSON events from server (ptt_start/end, presence)
 */
export function usePTT(ws) {
  const [transmitting, setTransmitting]   = useState(false);
  const [speakingUser, setSpeakingUser]   = useState(null);   // remote user transmitting
  const mediaRef   = useRef(null);
  const recorderRef = useRef(null);
  const audioCtxRef = useRef(null);

  // Play raw PCM bytes from server (other user's mic)
  const playAudio = useCallback((arrayBuffer) => {
    if (!audioCtxRef.current)
      audioCtxRef.current = new (window.AudioContext || window.webkitAudioContext)();
    const ctx = audioCtxRef.current;
    ctx.decodeAudioData(arrayBuffer.slice(0)).then((decoded) => {
      const src = ctx.createBufferSource();
      src.buffer = decoded;
      src.connect(ctx.destination);
      src.start();
    }).catch(() => {});
  }, []);

  // Handle JSON events from server
  const handleEvent = useCallback((evt) => {
    if (evt.type === "ptt_start") setSpeakingUser(evt.user_name || evt.user_id);
    if (evt.type === "ptt_end")   setSpeakingUser(null);
  }, []);

  // Start transmitting
  const startPTT = useCallback(async () => {
    if (!ws || ws.readyState !== WebSocket.OPEN) return;
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      mediaRef.current = stream;
      ws.send(JSON.stringify({ type: "ptt_start" }));
      setTransmitting(true);

      const recorder = new MediaRecorder(stream, { mimeType: "audio/webm;codecs=opus" });
      recorderRef.current = recorder;

      recorder.ondataavailable = (e) => {
        if (e.data.size > 0 && ws.readyState === WebSocket.OPEN) {
          e.data.arrayBuffer().then((buf) => ws.send(buf));
        }
      };
      recorder.start(100); // 100ms chunks
    } catch (err) {
      console.error("Mic error:", err);
    }
  }, [ws]);

  // Stop transmitting
  const stopPTT = useCallback(() => {
    if (!ws || ws.readyState !== WebSocket.OPEN) return;
    recorderRef.current?.stop();
    mediaRef.current?.getTracks().forEach((t) => t.stop());
    ws.send(JSON.stringify({ type: "ptt_end" }));
    setTransmitting(false);
  }, [ws]);

  return { transmitting, speakingUser, startPTT, stopPTT, playAudio, handleEvent };
}
